// ModuleErrorBoundary — class-component error boundary mounted per-route
// under ProtectedRoute (see routes.tsx). Catches render-time
// crashes in individual modules and renders <ErrorState> with a reload CTA.
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { i18n } from '../i18n/index.js';
import { ErrorState } from '../ui/index.js';

interface Props {
  children?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ModuleErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Intentionally minimal.md scope. Observability wiring lands later.
    console.error('[ModuleErrorBoundary]', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  override render(): ReactNode {
    if (this.state.error) {
      return (
        <ErrorState
          title={i18n.t('shell:moduleError.title')}
          text={i18n.t('shell:moduleError.text')}
          onRetry={this.reset}
          retryLabel={i18n.t('common:actions.retry')}
        />
      );
    }
    return this.props.children;
  }
}
